/* import __COLOCATED_TEMPLATE__ from './audience-selector.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import type AiContentSegmentsService from 'embercom/services/ai-content-segments-service';
import type AiContentSegment from 'embercom/models/ai-content-segment';
import type ImportSource from 'embercom/models/import-service/import-source';
import { put } from 'embercom/lib/ajax';
import type AppService from 'embercom/services/app-service';

interface Signature {
  Args: {
    modal: any;
    importSource: ImportSource;
    currentSegmentIds: number[];
    onModalClose: () => void;
  };
  Element: null;
}

export default class ShopifyAudienceSelector extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare aiContentSegmentsService: AiContentSegmentsService;
  @service declare appService: AppService;
  @service declare notificationsService: any;

  @tracked selectedSegmentIds: Set<number> = new Set(this.args.currentSegmentIds);
  @tracked isSaving = false;
  @tracked activeSegmentId: number | null = null;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    this.aiContentSegmentsService.loadSegments();
  }

  get segments(): AiContentSegment[] {
    return this.aiContentSegmentsService.segments ?? [];
  }

  get isLoading(): boolean {
    return this.aiContentSegmentsService.isLoading;
  }

  get hasChanges(): boolean {
    let current = new Set(this.args.currentSegmentIds);
    if (current.size !== this.selectedSegmentIds.size) {
      return true;
    }
    for (let id of current) {
      if (!this.selectedSegmentIds.has(id)) {
        return true;
      }
    }
    return false;
  }

  get isSaveDisabled(): boolean {
    return this.isSaving || !this.hasChanges;
  }

  @action
  isSegmentSelected(segment: AiContentSegment): boolean {
    return this.selectedSegmentIds.has(Number(segment.id));
  }

  @action
  toggleSegment(segment: AiContentSegment) {
    let id = Number(segment.id);
    let updated = new Set(this.selectedSegmentIds);
    if (updated.has(id)) {
      updated.delete(id);
    } else {
      updated.add(id);
    }
    this.selectedSegmentIds = updated;
  }

  @action
  selectEveryone() {
    this.selectedSegmentIds = new Set();
  }

  @action
  async save() {
    this.isSaving = true;
    try {
      await put('/ember/import_service/import_sources/update_import', {
        app_id: this.appService.app.id,
        import_source_id: this.args.importSource.id,
        content_segment_ids: Array.from(this.selectedSegmentIds),
      });
      await this.args.importSource.reload();
      this.notificationsService.notifyConfirmation(
        this.intl.t('knowledge-hub.sync-modal.shopify.audience-selector.save-success'),
      );
      this.args.onModalClose();
    } catch (e: unknown) {
      this.notificationsService.notifyError(
        this.intl.t('knowledge-hub.sync-modal.shopify.audience-selector.save-error'),
      );
    } finally {
      this.isSaving = false;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::SetupModal::SyncModal::Shopify::AudienceSelector': typeof ShopifyAudienceSelector;
    'knowledge-hub/setup-modal/sync-modal/shopify/audience-selector': typeof ShopifyAudienceSelector;
  }
}
